import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Checkbox } from '@ampm-7en-ai/ui/components/Checkbox';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL, getAuthHeaders } from '@/utils/api-config';
import { useSubscription } from '@/hooks/useSubscription';
import { useAIModels } from '@/hooks/useAIModels';

/**
 * One subscription plan: what it costs, what it allows, and which models and
 * integrations come with it.
 *
 * Grouped by the question each answers rather than by where the fields sit in
 * the payload — price, then limits, then what is switched on — because that is
 * the order someone pricing a plan thinks in.
 */

interface PlanConfig {
  limits: { ai_agents: number | null; team_seats: number | null; training_per_agent_kb: number | null };
  handoffs: { to_email: boolean; to_agent: boolean; to_integration: boolean };
  toggles: { embed_unlimited_websites: boolean; weblink_training: number | null };
  integrations: {
    freshdesk: boolean;
    zendesk: boolean;
    slack: boolean;
    zoho: boolean;
    zapier: boolean;
    salesforce: boolean;
    whatsapp: boolean;
    meta: boolean;
  };
  models: Record<string, boolean>;
}

interface Plan {
  id?: number;
  name: string;
  description: string;
  total_replies: number | null;
  price_monthly: number;
  duration_days_monthly: number;
  price_annual: number;
  duration_days_annual: number;
  config: PlanConfig;
  character_limit?: number | null;
  allowed_domains_to_embed?: number | null;
  api_enabled?: boolean;
  tools_enabled?: boolean;
  status?: string;
}

const EMPTY: Plan = {
  name: '',
  description: '',
  total_replies: 0,
  price_monthly: 0,
  duration_days_monthly: 30,
  price_annual: 0,
  duration_days_annual: 365,
  character_limit: 100,
  allowed_domains_to_embed: 0,
  api_enabled: false,
  tools_enabled: false,
  config: {
    limits: { ai_agents: 1, team_seats: 1, training_per_agent_kb: 100 },
    handoffs: { to_email: false, to_agent: false, to_integration: false },
    toggles: { embed_unlimited_websites: false, weblink_training: 0 },
    integrations: {
      freshdesk: false,
      zendesk: false,
      slack: false,
      zoho: false,
      zapier: false,
      salesforce: false,
      whatsapp: false,
      meta: false,
    },
    models: {},
  },
};

const INTEGRATIONS: { key: keyof PlanConfig['integrations']; label: string }[] = [
  { key: 'freshdesk', label: 'Freshdesk' },
  { key: 'zendesk', label: 'Zendesk' },
  { key: 'slack', label: 'Slack' },
  { key: 'zoho', label: 'Zoho' },
  { key: 'zapier', label: 'Zapier' },
  { key: 'salesforce', label: 'Salesforce' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'meta', label: 'Meta' },
];

const number = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Allowances may be left blank, which is saved as null rather than zero. */
const limit = (value: string) => (value === '' ? null : number(value));
const loadedLimit = (value: unknown) => (value === null || value === undefined || value === '' ? null : Number(value) || 0);

export default function SubscriptionPlanEditorV2() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const editing = Boolean(planId);

  const { refetchSubscriptionPlans } = useSubscription({ fetchCurrent: false, fetchAllPlans: false });
  const { allModelOptions, isLoading: loadingModels } = useAIModels(true);

  const [plan, setPlan] = React.useState<Plan>(EMPTY);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);

  const token = () => {
    const stored = localStorage.getItem('user');
    const found = stored ? JSON.parse(stored).accessToken : null;
    if (!found) throw new Error('You need to be signed in to do that.');
    return found;
  };

  React.useEffect(() => {
    if (!editing || !planId) return;

    const load = async () => {
      setLoading(true);
      setFailed(null);
      try {
        const response = await fetch(`${BASE_URL}subscriptions/plan/${planId}/`, {
          method: 'GET',
          headers: getAuthHeaders(token()),
        });
        if (!response.ok) throw new Error('Couldn’t load this plan.');

        const incoming = await response.json();
        const integrations = incoming.config?.integrations ?? {};
        setPlan({
          id: incoming.id,
          name: incoming.name,
          description: incoming.description || '',
          total_replies: loadedLimit(incoming.total_replies),
          price_monthly: Number(incoming.price_monthly) || 0,
          duration_days_monthly: Number(incoming.duration_days_monthly) || 30,
          price_annual: Number(incoming.price_annual) || 0,
          duration_days_annual: Number(incoming.duration_days_annual) || 365,
          character_limit: loadedLimit(incoming.character_limit),
          allowed_domains_to_embed: loadedLimit(incoming.allowed_domains_to_embed),
          api_enabled: incoming.api_enabled ?? false,
          tools_enabled: incoming.tools_enabled ?? false,
          status: incoming.status,
          config: {
            ...EMPTY.config,
            ...incoming.config,
            toggles: {
              ...EMPTY.config.toggles,
              ...incoming.config?.toggles,
              weblink_training: loadedLimit(incoming.config?.toggles?.weblink_training),
            },
            integrations: {
              freshdesk: integrations.freshdesk ?? false,
              zendesk: integrations.zendesk ?? false,
              slack: integrations.slack ?? false,
              zoho: integrations.zoho ?? false,
              zapier: integrations.zapier ?? false,
              salesforce: integrations.salesforce ?? false,
              whatsapp: integrations.whatsapp ?? false,
              // Instagram and Messenger were folded into one Meta switch.
              meta: integrations.meta ?? integrations.instagram ?? integrations.messenger ?? false,
            },
          },
        });
      } catch (error) {
        setFailed(error instanceof Error ? error.message : 'Couldn’t load this plan.');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [editing, planId]);

  const set = (change: Partial<Plan>) => setPlan((current) => ({ ...current, ...change }));
  const setConfig = (change: Partial<PlanConfig>) =>
    setPlan((current) => ({ ...current, config: { ...current.config, ...change } }));

  const integrationsOff = !plan.config.handoffs.to_integration;

  /** What would stop the server accepting it, said before it is sent. */
  const problems = React.useMemo(() => {
    const found: string[] = [];
    if (!plan.name.trim()) found.push('Give the plan a name.');
    if (plan.price_monthly === 0 && plan.duration_days_monthly <= 0)
      found.push('A free monthly plan still needs a duration in days.');
    if (plan.price_annual === 0 && plan.duration_days_annual <= 0)
      found.push('A free annual plan still needs a duration in days.');
    return found;
  }, [plan]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (problems.length > 0) return;

    setSaving(true);
    try {
      const payload = {
        name: plan.name,
        description: plan.description,
        total_replies: plan.total_replies,
        price_monthly: plan.price_monthly,
        duration_days_monthly: plan.duration_days_monthly,
        price_annual: plan.price_annual,
        duration_days_annual: plan.duration_days_annual,
        character_limit: plan.character_limit,
        allowed_domains_to_embed: plan.allowed_domains_to_embed,
        api_enabled: plan.api_enabled,
        tools_enabled: plan.tools_enabled,
        config: plan.config,
      };

      const response = await fetch(
        editing ? `${BASE_URL}subscriptions/update/${planId}/` : `${BASE_URL}subscriptions/create/`,
        { method: editing ? 'PUT' : 'POST', headers: getAuthHeaders(token()), body: JSON.stringify(payload) }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || `The plan wasn’t ${editing ? 'updated' : 'created'}.`);
      }

      const body = await response.json();
      const saved = editing ? body : body.data;
      if (!editing && !saved?.id) {
        throw new Error('The plan was created but its ID was missing. Reload the plans list before trying again.');
      }

      queryClient.setQueryData(['subscriptionPlans'], (old: unknown) => {
        if (!Array.isArray(old)) return [saved];
        return editing ? old.map((one) => (one.id === saved.id ? saved : one)) : [...old, saved];
      });
      refetchSubscriptionPlans?.();

      toast({ title: `${plan.name} ${editing ? 'updated' : 'created'}` });
      if (!editing) {
        navigate(`/v2/settings/platform/subscription-plan/${saved.id}`, { replace: true });
      }
    } catch (error) {
      toast({
        title: 'That didn’t work',
        description: error instanceof Error ? error.message : 'The plan wasn’t saved.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PlatformSettingsLayout
      title={editing ? 'Edit plan' : 'New plan'}
      description="What a workspace pays, and what that buys them."
    >
      <Button variant="ghost" className="-ml-2 mb-3 gap-1.5" onClick={() => navigate('/v2/settings/platform/billing')}>
        <ArrowLeft /> All plans
      </Button>

      {failed && (
        <Callout tone="danger" className="mb-4" title="Couldn’t load this plan">
          {failed}
        </Callout>
      )}

      <form onSubmit={save} className="space-y-4">
        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">What it is</h2>
          {loading ? (
            <div className="mt-5 space-y-4">
              <Skeleton className="h-[38px] w-full rounded-ui-md" />
              <Skeleton className="h-20 w-full rounded-ui-md" />
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <Field label="Name" htmlFor="plan-name">
                <Input id="plan-name" value={plan.name} onChange={(e) => set({ name: e.target.value })} placeholder="Plus" />
              </Field>
              <Field label="Description" htmlFor="plan-description">
                <Textarea
                  id="plan-description"
                  rows={2}
                  value={plan.description}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="For teams running several agents across channels."
                />
              </Field>
            </div>
          )}
        </Panel>

        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">Price</h2>
          <p className="mt-0.5 text-ui-xs text-ui-fg-muted">
            A price of zero makes it free, but it still needs a duration so the period can end.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Monthly price" htmlFor="price-monthly">
              <Input
                id="price-monthly"
                type="number"
                min={0}
                step="0.01"
                value={plan.price_monthly}
                onChange={(e) => set({ price_monthly: number(e.target.value) })}
              />
            </Field>
            <Field label="Monthly duration" htmlFor="duration-monthly" hint="In days.">
              <Input
                id="duration-monthly"
                type="number"
                min={1}
                value={plan.duration_days_monthly}
                onChange={(e) => set({ duration_days_monthly: number(e.target.value) })}
              />
            </Field>
            <Field label="Annual price" htmlFor="price-annual">
              <Input
                id="price-annual"
                type="number"
                min={0}
                step="0.01"
                value={plan.price_annual}
                onChange={(e) => set({ price_annual: number(e.target.value) })}
              />
            </Field>
            <Field label="Annual duration" htmlFor="duration-annual" hint="In days.">
              <Input
                id="duration-annual"
                type="number"
                min={1}
                value={plan.duration_days_annual}
                onChange={(e) => set({ duration_days_annual: number(e.target.value) })}
              />
            </Field>
          </div>
        </Panel>

        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">Allowances</h2>
          <p className="mt-0.5 text-ui-xs text-ui-fg-muted">What the workspace may build. Zero means none. Leave blank for no limit.</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Replies" htmlFor="total-replies" hint="Per period.">
              <Input
                id="total-replies"
                type="number"
                min={0}
                value={plan.total_replies ?? ''}
                onChange={(e) => set({ total_replies: limit(e.target.value) })}
              />
            </Field>
            <Field label="Agents" htmlFor="limit-agents">
              <Input
                id="limit-agents"
                type="number"
                min={0}
                value={plan.config.limits.ai_agents ?? ''}
                onChange={(e) => setConfig({ limits: { ...plan.config.limits, ai_agents: limit(e.target.value) } })}
              />
            </Field>
            <Field label="Team seats" htmlFor="limit-seats">
              <Input
                id="limit-seats"
                type="number"
                min={0}
                value={plan.config.limits.team_seats ?? ''}
                onChange={(e) => setConfig({ limits: { ...plan.config.limits, team_seats: limit(e.target.value) } })}
              />
            </Field>
            <Field label="Training per agent" htmlFor="limit-training" hint="In KB.">
              <Input
                id="limit-training"
                type="number"
                min={0}
                value={plan.config.limits.training_per_agent_kb ?? ''}
                onChange={(e) =>
                  setConfig({ limits: { ...plan.config.limits, training_per_agent_kb: limit(e.target.value) } })
                }
              />
            </Field>
            <Field label="Character limit" htmlFor="character-limit">
              <Input
                id="character-limit"
                type="number"
                min={0}
                value={plan.character_limit ?? ''}
                onChange={(e) => set({ character_limit: limit(e.target.value) })}
              />
            </Field>
            <Field label="Allowed domains to embed" htmlFor="allowed-domains">
              <Input
                id="allowed-domains"
                type="number"
                min={0}
                value={plan.allowed_domains_to_embed ?? ''}
                onChange={(e) => set({ allowed_domains_to_embed: limit(e.target.value) })}
              />
            </Field>
            <Field label="Allowed URLs to train" htmlFor="weblink-training" hint="URLs that may be scraped as knowledge.">
              <Input
                id="weblink-training"
                type="number"
                min={0}
                value={plan.config.toggles.weblink_training ?? ''}
                onChange={(e) =>
                  setConfig({ toggles: { ...plan.config.toggles, weblink_training: limit(e.target.value) } })
                }
              />
            </Field>
          </div>
        </Panel>

        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">Handing a conversation over</h2>
          <p className="mt-0.5 text-ui-xs text-ui-fg-muted">Where an agent on this plan may send a conversation it can’t finish.</p>
          <div className="mt-4 space-y-2">
            <ToggleRow
              label="To email"
              description="Send a transcript to an inbox."
              checked={plan.config.handoffs.to_email}
              onCheckedChange={(to_email) => setConfig({ handoffs: { ...plan.config.handoffs, to_email } })}
            />
            <ToggleRow
              label="To another agent"
              description="Pass it to a different agent in the same workspace."
              checked={plan.config.handoffs.to_agent}
              onCheckedChange={(to_agent) => setConfig({ handoffs: { ...plan.config.handoffs, to_agent } })}
            />
            <ToggleRow
              label="To an integration"
              description="Raise it in a ticketing or chat tool. The integrations below need this on."
              checked={plan.config.handoffs.to_integration}
              onCheckedChange={(to_integration) => setConfig({ handoffs: { ...plan.config.handoffs, to_integration } })}
            />
          </div>

          <div className="mt-5">
            <h3 className="text-ui-sm font-medium text-ui-fg">Integrations</h3>
            <p className="mt-0.5 text-ui-xs text-ui-fg-muted">
              {integrationsOff
                ? 'Switch on “to an integration” above to choose which ones.'
                : 'The tools this plan may hand a conversation to.'}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {INTEGRATIONS.map(({ key, label }) => (
                <Checkbox
                  key={key}
                  label={label}
                  checked={plan.config.integrations[key]}
                  disabled={integrationsOff}
                  onChange={(event) =>
                    setConfig({ integrations: { ...plan.config.integrations, [key]: event.target.checked } })
                  }
                />
              ))}
            </div>
          </div>
        </Panel>

        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">Models</h2>
          <p className="mt-0.5 text-ui-xs text-ui-fg-muted">Which models an agent on this plan may be set to.</p>
          {loadingModels ? (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-6 w-full rounded-ui-sm" />
              ))}
            </div>
          ) : (allModelOptions ?? []).length === 0 ? (
            <p className="mt-3 text-ui-sm text-ui-fg-muted">No models are configured on the platform yet.</p>
          ) : (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(allModelOptions ?? []).map((model) => {
                const key = String(model.value ?? model.label);
                return (
                  <Checkbox
                    key={key}
                    label={model.label}
                    checked={Boolean(plan.config.models[key])}
                    onChange={(event) => setConfig({ models: { ...plan.config.models, [key]: event.target.checked } })}
                  />
                );
              })}
            </div>
          )}
        </Panel>

        <Panel padding="md">
          <ToggleRow
            label="API access"
            description="Lets the workspace call the platform's API with its own key."
            checked={plan.api_enabled ?? false}
            onCheckedChange={(api_enabled) => set({ api_enabled })}
          />
          <ToggleRow
            label="Tools access"
            description="Enables tools for workspaces on this plan."
            checked={plan.tools_enabled ?? false}
            onCheckedChange={(tools_enabled) => set({ tools_enabled })}
          />
        </Panel>

        {problems.length > 0 && (
          <Callout tone="warning" title="Before this can be saved">
            <ul className="space-y-0.5">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Callout>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate('/v2/settings/platform/billing')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving || loading || problems.length > 0}>
            {saving ? 'Saving…' : editing ? 'Save plan' : 'Create plan'}
          </Button>
        </div>
      </form>
    </PlatformSettingsLayout>
  );
}
